import { useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'

// ─── Portal dropdown ──────────────────────────────────────────────────────────

export interface DropdownMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  children: React.ReactNode
  align?: 'left' | 'right'
  width?: number
}

export function DropdownMenu({ anchorEl, open, onClose, children, align = 'right', width = 224 }: DropdownMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      const onMD = (e: MouseEvent) => {
        if (!menuRef.current?.contains(e.target as Node)) onClose()
      }
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
      document.addEventListener('mousedown', onMD)
      document.addEventListener('keydown', onKey)
      return () => {
        document.removeEventListener('mousedown', onMD)
        document.removeEventListener('keydown', onKey)
      }
    }, 0)
    return () => clearTimeout(id)
  }, [open, onClose])

  if (!open || !anchorEl) return null

  const rect = anchorEl.getBoundingClientRect()
  const style: React.CSSProperties = {
    position: 'fixed',
    top: rect.bottom + 4,
    width,
    zIndex: 9000,
  }
  if (align === 'right') {
    style.right = Math.max(8, window.innerWidth - rect.right)
  } else {
    style.left = Math.max(8, rect.left)
  }

  return createPortal(
    <motion.div
      ref={menuRef}
      className="dm"
      style={style}
      initial={{ opacity: 0, y: -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>,
    document.body
  )
}

// ─── Building blocks ─────────────────────────────────────────────────────────

export function DmItem({
  icon: Icon, label, onClick, danger, disabled, checked, sub,
}: {
  icon?: React.ElementType
  label: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  checked?: boolean
  sub?: string
}) {
  return (
    <button
      className={[
        'dm-item',
        danger ? 'dm-item--danger' : '',
        disabled ? 'dm-item--disabled' : '',
        sub ? 'dm-item--two-line' : '',
      ].filter(Boolean).join(' ')}
      onClick={disabled ? undefined : onClick}
    >
      {Icon && <Icon size={14} className="dm-item__icon" />}
      {sub ? (
        <>
          <span className="dm-item__main">{label}</span>
          <span className="dm-item__sub">{sub}</span>
        </>
      ) : (
        <span style={{ flex: 1 }}>{label}</span>
      )}
      {checked !== undefined && checked && <Check size={12} className="dm-item__check" />}
    </button>
  )
}

export function DmDivider() {
  return <div className="dm-divider" />
}

export function DmSectionLabel({ label }: { label: string }) {
  return <div className="dm-section-label">{label}</div>
}
